import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/common/components/ui/button";
import { Input } from "@/common/components/ui/input";
import { Label } from "@/common/components/ui/label";
import { Checkbox } from "@/common/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/common/components/ui/select";
import { ArrowLeft, Save, Loader2, Package } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import Loader from "@/common/components/ui/loader";
import { GoldService } from "@/services/APIs";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/common/components/ui/card";
import finEaseHomeNavigationRoutes from "@/projects/FinEaseHomeAndAdmin/utils/navigationRoutes";

const CreateDeliveryMasterPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const isViewMode = location.pathname.includes("/view/");
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);

  // Form state
  const [form, setForm] = useState<any>({
    priority: "1",
  });

  useEffect(() => {
    if (isEditMode || isViewMode) {
      if (id) {
        fetchDetails(id);
      }
    }
  }, [id, isEditMode, isViewMode]);

  const fetchDetails = async (recordId: string) => {
    setFetching(true);
    try {
      const response: any = await GoldService.getDeliveryMasterById(recordId);
      if (response) {
        setForm({
          ...response,
          priority: response.priority?.toString(),

          // BVC mapping
          delivery_pincode: response.pincode,
          state:
            response.priority === "1" ? response.bvc_state : response.bd_state,
          region:
            response.priority === "1"
              ? response.bvc_region
              : response.bd_region,
          service_days: response.bvc_service_days,
          delivery_city: response.bvc_delivery_city,
          bvc_delivery_loc: response.bvc_delivery_loc,
          delivery_area_name: response.bvc_delivery_area_name,
          pickup_service: response.bvc_pickup_service === "Yes",
          delivery_service: response.bvc_delivery_service === "Yes",
          cod_delivery_service: response.bvc_cod_delivery_service === "Yes",
          reverse_pickup_service: response.bvc_reverse_pickup_service === "Yes",

          // Bluedart mapping
          cpincode: response.pincode,
          carea: response.bd_carea,
          cscrcd: response.bd_cscrcd,
          careadesc: response.bd_careadesc,
          city: response.bd_city,
          bdel_loc: response.bd_bdel_loc,
          cstatecd: response.bd_cstatecd,
          gstcode: response.bd_gstcode,
          zone: response.bd_zone,
          cloctype: response.bd_cloctype,
          dstarcd: response.bd_dstarcd,
          ccrcrdscr: response.bd_ccrcrdscr,
          csfczone: response.bd_csfczone,
          sfczonedesc: response.bd_sfczonedesc,
          subregion: response.bd_subregion,
          cservflag: response.bd_cservflag,
          ctel: response.bd_ctel,
          cnt: response.bd_cnt,
          newzone: response.bd_newzone,
          dod_apex: response.bd_dod_apex,
          dod_sfc: response.bd_dod_sfc,
          fod_apex: response.bd_fod_apex,
          fod_sfc: response.bd_fod_sfc,
          form: response.bd_form,
          octroi: response.bd_octroi,
          ndpdval: response.bd_ndpdval,
          intflag: response.bd_intflag,
          otmdel: response.bd_otmdel,
          cebagdst: response.bd_cebagdst,
          domestic: response.bd_domestic === "Yes",
          apex: response.bd_apex === "Yes",
          surface: response.bd_surface === "Yes",
          cod: response.bd_cod === "Yes",
          creditcard: response.bd_creditcard === "Yes",
          tdd: response.bd_tdd === "Yes",
          tddapex1200: response.bd_tddapex1200 === "Yes",
        });
      }
    } catch (_error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch details",
      });
    } finally {
      setFetching(false);
    }
  };

  const handleChange = (field: string, value: any) => {
    if (isViewMode) {
      return;
    }
    setForm((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isViewMode) {
      return;
    }

    if (!form.priority) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Please select a Priority",
      });
      return;
    }

    if (form.priority === "1") {
      if (
        form.delivery_pincode?.length !== 6 ||
        isNaN(Number(form.delivery_pincode))
      ) {
        toast({
          variant: "destructive",
          title: "Validation Error",
          description: "Valid 6-digit Pincode required",
        });
        return;
      }
      if (!form.state) {
        toast({
          variant: "destructive",
          title: "Validation Error",
          description: "State required",
        });
        return;
      }
      if (!form.region) {
        toast({
          variant: "destructive",
          title: "Validation Error",
          description: "Region required",
        });
        return;
      }
      if (!form.service_days) {
        toast({
          variant: "destructive",
          title: "Validation Error",
          description: "Service Days required",
        });
        return;
      }
    }

    if (form.priority === "2") {
      if (form.cpincode?.length !== 6 || isNaN(Number(form.cpincode))) {
        toast({
          variant: "destructive",
          title: "Validation Error",
          description: "Valid 6-digit Pincode required",
        });
        return;
      }
      if (!form.state) {
        toast({
          variant: "destructive",
          title: "Validation Error",
          description: "State required",
        });
        return;
      }
      if (!form.region) {
        toast({
          variant: "destructive",
          title: "Validation Error",
          description: "Region required",
        });
        return;
      }
    }

    setLoading(true);
    try {
      const payload: any = {
        priority: Number(form.priority),
      };

      if (form.priority === "1") {
        Object.assign(payload, {
          pincode: form.delivery_pincode,
          bvc_state: form.state,
          bvc_region: form.region,
          bvc_service_days: form.service_days,
          bvc_delivery_city: form.delivery_city,
          bvc_delivery_loc: form.bvc_delivery_loc,
          bvc_delivery_area_name: form.delivery_area_name,
          bvc_pickup_service: form.pickup_service ? "Yes" : "No",
          bvc_delivery_service: form.delivery_service ? "Yes" : "No",
          bvc_cod_delivery_service: form.cod_delivery_service ? "Yes" : "No",
          bvc_reverse_pickup_service: form.reverse_pickup_service
            ? "Yes"
            : "No",
        });
      }

      if (form.priority === "2") {
        Object.assign(payload, {
          pincode: form.cpincode,
          bd_state: form.state,
          bd_region: form.region,
          bd_carea: form.carea,
          bd_cscrcd: form.cscrcd,
          bd_careadesc: form.careadesc,
          bd_city: form.city,
          bd_bdel_loc: form.bdel_loc,
          bd_cstatecd: form.cstatecd,
          bd_gstcode: form.gstcode,
          bd_zone: form.zone,
          bd_cloctype: form.cloctype,
          bd_dstarcd: form.dstarcd,
          bd_ccrcrdscr: form.ccrcrdscr,
          bd_csfczone: form.csfczone,
          bd_sfczonedesc: form.sfczonedesc,
          bd_subregion: form.subregion,
          bd_cservflag: form.cservflag,
          bd_ctel: form.ctel,
          bd_cnt: form.cnt,
          bd_newzone: form.newzone,
          bd_dod_apex: form.dod_apex,
          bd_dod_sfc: form.dod_sfc,
          bd_fod_apex: form.fod_apex,
          bd_fod_sfc: form.fod_sfc,
          bd_form: form.form,
          bd_octroi: form.octroi,
          bd_ndpdval: form.ndpdval,
          bd_intflag: form.intflag,
          bd_otmdel: form.otmdel,
          bd_cebagdst: form.cebagdst,
          bd_domestic: form.domestic ? "Yes" : "No",
          bd_apex: form.apex ? "Yes" : "No",
          bd_surface: form.surface ? "Yes" : "No",
          bd_cod: form.cod ? "Yes" : "No",
          bd_creditcard: form.creditcard ? "Yes" : "No",
          bd_tdd: form.tdd ? "Yes" : "No",
          bd_tddapex1200: form.tddapex1200 ? "Yes" : "No",
        });
      }

      let response;
      if (isEditMode && id) {
        response = await GoldService.updateDeliveryMaster(id, payload);
      } else {
        response = await GoldService.createDeliveryMaster(payload);
      }

      if (response && (response.success || response.id)) {
        toast({
          title: "Success",
          description: `Record ${isEditMode ? "updated" : "created"} successfully`,
        });
        navigate(finEaseHomeNavigationRoutes.PHYSICAL_GOLD_DELIVERY_MASTER);
      } else {
        throw new Error(response?.message ?? "Operation failed");
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message ?? "Failed to save record",
      });
    } finally {
      setLoading(false);
    }
  };

  const renderBVCFields = () => (
    <>
      <div className="space-y-2">
        <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
          Delivery Pincode *
        </Label>
        <Input
          placeholder="6-digit Pincode"
          value={form.delivery_pincode ?? ""}
          onChange={e => handleChange("delivery_pincode", e.target.value)}
          maxLength={6}
          disabled={isViewMode}
          className="h-11 border-slate-200 font-medium placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400"
        />
      </div>
      <div className="space-y-2">
        <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
          State *
        </Label>
        <Select
          disabled={isViewMode}
          value={form.state ?? ""}
          onValueChange={v => handleChange("state", v)}
        >
          <SelectTrigger className="h-11 border-slate-200">
            <SelectValue placeholder="Select State" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Maharashtra">Maharashtra</SelectItem>
            <SelectItem value="Gujarat">Gujarat</SelectItem>
            <SelectItem value="Karnataka">Karnataka</SelectItem>
            <SelectItem value="Delhi">Delhi</SelectItem>
            <SelectItem value="Tamil Nadu">Tamil Nadu</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
          Region *
        </Label>
        <Select
          disabled={isViewMode}
          value={form.region ?? ""}
          onValueChange={v => handleChange("region", v)}
        >
          <SelectTrigger className="h-11 border-slate-200">
            <SelectValue placeholder="Select Region" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="West">West</SelectItem>
            <SelectItem value="North">North</SelectItem>
            <SelectItem value="South">South</SelectItem>
            <SelectItem value="East">East</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
          Service Days *
        </Label>
        <Select
          disabled={isViewMode}
          value={form.service_days ?? ""}
          onValueChange={v => handleChange("service_days", v)}
        >
          <SelectTrigger className="h-11 border-slate-200">
            <SelectValue placeholder="Select Days" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Daily Service">Daily</SelectItem>
            <SelectItem value="Mon-Wed-Fri">Mon-Wed-Fri</SelectItem>
            <SelectItem value="Tue-Fri">Tue-Fri</SelectItem>
            <SelectItem value="Custom">Custom</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
          Delivery City
        </Label>
        <Input
          disabled={isViewMode}
          placeholder="City Name"
          value={form.delivery_city ?? ""}
          onChange={e => handleChange("delivery_city", e.target.value)}
          className="h-11 border-slate-200 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400"
        />
      </div>
      <div className="space-y-2">
        <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
          BVC Delivery LOC
        </Label>
        <Input
          disabled={isViewMode}
          placeholder="LOC Code"
          value={form.bvc_delivery_loc ?? ""}
          onChange={e => handleChange("bvc_delivery_loc", e.target.value)}
          className="h-11 border-slate-200 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400"
        />
      </div>
      <div className="space-y-2">
        <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
          Delivery Area Name
        </Label>
        <Input
          disabled={isViewMode}
          placeholder="Area Name"
          value={form.delivery_area_name ?? ""}
          onChange={e => handleChange("delivery_area_name", e.target.value)}
          className="h-11 border-slate-200 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400"
        />
      </div>
      <div className="col-span-1 mt-2 grid grid-cols-2 gap-4 md:col-span-2 md:grid-cols-4">
        {[
          { key: "pickup_service", label: "Pickup Service" },
          { key: "delivery_service", label: "Delivery Service" },
          { key: "cod_delivery_service", label: "COD Delivery" },
          { key: "reverse_pickup_service", label: "Reverse Pickup" },
        ].map(opt => (
          <div key={opt.key} className="flex items-center space-x-2">
            <Checkbox
              id={opt.key}
              disabled={isViewMode}
              checked={form[opt.key] ?? false}
              onCheckedChange={c => handleChange(opt.key, c)}
            />
            <Label htmlFor={opt.key} className="text-sm">
              {opt.label}
            </Label>
          </div>
        ))}
      </div>
    </>
  );

  const renderBluedartFields = () => {
    const textFields = [
      { key: "carea", label: "CAREA" },
      { key: "cscrcd", label: "CSCRCD" },
      { key: "careadesc", label: "CAREADESC" },
      { key: "city", label: "CITY" },
      { key: "bdel_loc", label: "BDEL_LOC" },
      { key: "cstatecd", label: "CSTATECD" },
      { key: "gstcode", label: "GSTCODE" },
      { key: "zone", label: "ZONE" },
      { key: "cloctype", label: "CLOCTYPE" },
      { key: "dstarcd", label: "DSTARCD" },
      { key: "ccrcrdscr", label: "CCRCRDSCR" },
      { key: "csfczone", label: "CSFCZONE" },
      { key: "sfczonedesc", label: "SFCZONEDESC" },
      { key: "subregion", label: "SUBREGION" },
      { key: "cservflag", label: "CSERVFLAG" },
      { key: "ctel", label: "CTEL" },
      { key: "cnt", label: "CNT" },
      { key: "newzone", label: "NEWZONE" },
      { key: "dod_apex", label: "DOD_APEX" },
      { key: "dod_sfc", label: "DOD_SFC" },
      { key: "fod_apex", label: "FOD_APEX" },
      { key: "fod_sfc", label: "FOD_SFC" },
      { key: "form", label: "FORM" },
      { key: "octroi", label: "OCTROI" },
      { key: "ndpdval", label: "NDPDVAL" },
      { key: "intflag", label: "INTFLAG" },
      { key: "otmdel", label: "OTMDEL" },
      { key: "cebagdst", label: "CEBAGDST" },
    ];

    const boolFields = [
      { key: "domestic", label: "DOMESTIC" },
      { key: "apex", label: "APEX" },
      { key: "surface", label: "SURFACE" },
      { key: "cod", label: "COD" },
      { key: "creditcard", label: "CREDITCARD" },
      { key: "tdd", label: "TDD" },
      { key: "tddapex1200", label: "TDDAPEX1200" },
    ];

    return (
      <>
        <div className="space-y-2">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
            CPINCODE *
          </Label>
          <Input
            placeholder="6-digit Pincode"
            value={form.cpincode ?? ""}
            onChange={e => handleChange("cpincode", e.target.value)}
            maxLength={6}
            disabled={isViewMode}
            className="h-11 border-slate-200 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
            State *
          </Label>
          <Input
            disabled={isViewMode}
            placeholder="State"
            value={form.state ?? ""}
            onChange={e => handleChange("state", e.target.value)}
            className="h-11 border-slate-200 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
            Region *
          </Label>
          <Input
            disabled={isViewMode}
            placeholder="Region"
            value={form.region ?? ""}
            onChange={e => handleChange("region", e.target.value)}
            className="h-11 border-slate-200 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400"
          />
        </div>
        {textFields.map(f => (
          <div key={f.key} className="space-y-2">
            <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
              {f.label}
            </Label>
            <Input
              disabled={isViewMode}
              placeholder={f.label}
              value={form[f.key] ?? ""}
              onChange={e => handleChange(f.key, e.target.value)}
              className="h-11 border-slate-200 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400"
            />
          </div>
        ))}
        <div className="col-span-1 mt-2 grid grid-cols-2 gap-4 md:col-span-2 md:grid-cols-4">
          {boolFields.map(opt => (
            <div key={opt.key} className="flex items-center space-x-2">
              <Checkbox
                id={opt.key}
                disabled={isViewMode}
                checked={form[opt.key] ?? false}
                onCheckedChange={c => handleChange(opt.key, c)}
              />
              <Label htmlFor={opt.key} className="text-sm">
                {opt.label}
              </Label>
            </div>
          ))}
        </div>
      </>
    );
  };

  return (
    <div className="flex-1 pb-12">
      {(fetching || loading) && <Loader />}
      <div className="flex flex-col">
        {/* Header */}
        <div className="sticky top-0 z-30 border-b border-slate-200 bg-white">
          <div className="mx-auto w-full max-w-7xl">
            <div className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center gap-5">
                <Button
                  variant="outline"
                  size="icon"
                  type="button"
                  onClick={() =>
                    navigate(
                      finEaseHomeNavigationRoutes.PHYSICAL_GOLD_DELIVERY_MASTER
                    )
                  }
                  className="h-10 w-10 border-slate-200 shadow-sm transition-all hover:border-slate-300 hover:bg-slate-50"
                >
                  <ArrowLeft className="h-5 w-5 text-slate-600" />
                </Button>
                <div>
                  <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                    {isViewMode
                      ? "View Delivery Master"
                      : isEditMode
                        ? "Edit Delivery Master"
                        : "Add Delivery Master"}
                  </h1>
                </div>
              </div>
              <div className="flex items-center gap-4">
                {!isViewMode && (
                  <Button
                    type="button"
                    onClick={handleSave}
                    disabled={loading || fetching}
                    className="h-11 gap-2.5 bg-primary px-8 text-sm font-bold text-white shadow-lg shadow-primary/20 transition-all hover:-translate-y-0.5"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    Save Record
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="mx-auto w-full max-w-7xl flex-1 space-y-8 p-6">
          <Card className="rounded-xl border border-slate-200 bg-white shadow-sm">
            <CardHeader className="border-b border-slate-100 pb-4">
              <CardTitle className="flex items-center gap-2 text-base font-bold text-slate-800">
                <Package className="h-5 w-5 text-primary" />
                Delivery Information
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-x-8 gap-y-6 p-6 md:grid-cols-2">
              <div className="col-span-1 max-w-md space-y-2 md:col-span-2">
                <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  Courier Partner *
                </Label>
                <Select
                  value={form.priority?.toString()}
                  onValueChange={v => handleChange("priority", v)}
                  disabled={isEditMode || isViewMode} // Usually we don't allow changing partner type on edit
                >
                  <SelectTrigger className="h-11 border-slate-200">
                    <SelectValue placeholder="Select Partner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">BVC</SelectItem>
                    <SelectItem value="2">Bluedart</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {form.priority === "1" && renderBVCFields()}
              {form.priority === "2" && renderBluedartFields()}

              {!form.priority && (
                <div className="col-span-1 rounded-lg bg-slate-50 p-8 text-center text-slate-500 md:col-span-2">
                  Please select a Courier Partner to continue.
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CreateDeliveryMasterPage;
